import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import api from '../services/api';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    return {
      isConnected: false,
      closedReason: null,
      dashboardData: null,
      trendsData: null,
      notifications: [],
      unreadCount: 0,
      lastUpdate: null,
      requestTrends: () => {},
      subscribe: () => {},
    };
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  // Session auth is httpOnly-cookie based (Phase-1 XSS hardening), so
  // AuthContext deliberately does NOT expose a bearer token. For the
  // WS handshake — which can't carry cookies from a wss:// URL query
  // param — we fetch a short-lived JWT ticket via
  // ``GET /api/auth/ws-ticket`` on every (re)connect. The httpOnly
  // cookie authenticates the ticket request; the ticket then
  // authenticates the WS handshake.
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [closedReason, setClosedReason] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(async () => {
    if (!user || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // 1) Mint a short-lived ticket. If the cookie is invalid this
    // returns 401 — no point opening a WS that would immediately
    // 4001-close.
    let ticket;
    try {
      const resp = await api.get('/auth/ws-ticket');
      ticket = resp?.data?.ticket;
    } catch (err) {
      // 401 → user not authenticated. 403 → the caller doesn't have
      // permission to run the get_current_user dependency. Either
      // way we don't retry, and RealTimeIndicator hides itself via
      // closedReason.
      const status = err?.response?.status;
      setClosedReason(status === 401 ? 'unauthenticated' : 'ticket_failed');
      logger.debug('[websocket] ws-ticket fetch failed', {
        op: 'ws_ticket', status, message: err?.message,
      });
      return;
    }
    if (!ticket) {
      setClosedReason('ticket_missing');
      return;
    }

    // 2) Build the wss:// URL and open.
    const sameOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    const backendUrl = sameOrigin || process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/dashboard?token=${encodeURIComponent(ticket)}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Track handshake vs. post-handshake close so a browser-side
      // event.code=1006 shortly after new WebSocket(...) can be treated
      // as an authentication failure (some proxies swallow the
      // 4001/4403 close code and surface 1006 to the JS layer).
      const openedAtRef = { current: null };

      ws.onopen = () => {
        openedAtRef.current = Date.now();
        setIsConnected(true);
        setClosedReason(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleMessage(message);
        } catch (error) {
          logger.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;

        // Auth/authorization close codes (4001, 4403) — do NOT reconnect.
        // The socket is refused because the caller is unauthenticated or
        // (for AZAI staff) has no active elevation for the selected
        // agency. Retrying every few seconds would spam the backend and
        // leave the "Offline" indicator flashing forever. A user gesture
        // (re-login / request elevation) fires an "elevation-changed"
        // event elsewhere that the caller can subscribe to.
        if (event.code === 4001 || event.code === 4403) {
          reconnectAttemptsRef.current = maxReconnectAttempts;
          setClosedReason(event.code === 4001 ? 'unauthenticated' : 'no_elevation');
          return;
        }

        // A 1006 (abnormal closure) shortly after `new WebSocket(...)`
        // and BEFORE ws.onopen fired usually means the handshake was
        // rejected by an intermediate proxy that stripped our
        // 4001/4403 code. Treat as auth failure — no reconnect.
        const openedAt = openedAtRef.current;
        if (
          event.code === 1006
          && !openedAt
        ) {
          reconnectAttemptsRef.current = maxReconnectAttempts;
          setClosedReason('handshake_rejected');
          logger.debug('[websocket] handshake rejected (1006 before open)', {
            op: 'ws_close_handshake_reject',
          });
          return;
        }

        // Attempt reconnection with exponential backoff.
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        logger.error('WebSocket error:', error);
      };
    } catch (error) {
      logger.error('Failed to create WebSocket:', error);
    }
  }, [user]);

  const handleMessage = (message) => {
    setLastUpdate(new Date());

    switch (message.type) {
      case 'initial_data':
      case 'dashboard_update':
        setDashboardData(message.data);
        break;
      case 'trends_data':
        setTrendsData(message.data);
        break;
      case 'notification':
        // Handle real-time notification
        if (message.notification) {
          setNotifications(prev => [message.notification, ...prev.slice(0, 49)]);
          setUnreadCount(prev => prev + 1);
          
          // Show toast notification
          toast(message.notification.title, {
            description: message.notification.message,
            duration: 5000,
          });
        }
        break;
      case 'claim_created':
      case 'claim_updated':
      case 'patient_created':
        // Trigger a dashboard refresh on these events
        setDashboardData(prev => ({
          ...prev,
          _lastEvent: message.type,
          _eventData: message.data
        }));
        break;
      case 'pong':
        // Keep-alive response
        break;
      default:
        break;
    }
  };

  const requestTrends = useCallback((days = 30, agencyId = null) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'get_trends',
        days,
        agency_id: agencyId
      }));
    }
  }, []);

  const subscribe = useCallback((channel) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe',
        channel
      }));
    }
  }, []);

  // Connect when the user session is established (httpOnly cookie).
  useEffect(() => {
    if (user) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user, connect]);

  // Keep-alive ping
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, [isConnected]);

  const value = {
    isConnected,
    closedReason,
    dashboardData,
    trendsData,
    notifications,
    unreadCount,
    lastUpdate,
    requestTrends,
    subscribe,
    setUnreadCount,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
